import { Submission, Review } from 'src/app/model/paper';
import { ReviewStatus } from 'src/app/enum/review.status';
import { UserEvent } from 'src/app/model/user.event';
import { TopicInterest } from 'src/app/model/user.event.topics.interest';
import { AssignMotive, Motives, Translations } from 'src/app/enum/assign.motives';

import { UserAssignInfo, SubmissionAssignSuggestions, WeightFunction, AssignmentTestFunction } from './types';


export function topicsMatchFunction(userPreference: UserEvent, submission: Submission, positiveMatchWeight: number = 1, negativeMatchWeight: number = 1): number {
  // Weight function
  let positiveSum = 0, negativeSum = 0;

  userPreference.topicsInterest.forEach(topic => {
    if (topic.interest > TopicInterest.neutral && submission.topics.some(t => t.id === topic.eventTopic.id)) {
      positiveSum++;
    } else
    if (topic.interest < TopicInterest.neutral && submission.topics.some(t => t.id === topic.eventTopic.id)) {
      negativeSum++;
    }
  });
  return positiveMatchWeight*positiveSum - negativeMatchWeight*negativeSum;
}


export function classicAssignmentSuggestion(submissionMatrix: Array<SubmissionAssignSuggestions>,
  numberOfCommittees: number, numberOfReviewsRequired: number) {

  // as in the JEMS1
  const flatMatrix: Array<UserAssignInfo> = [];

  submissionMatrix.forEach(submission => {
    flatMatrix.push(...submission.possiblesAssign);
  });

  // Desempate ALEATÓRIO: embaralha (Fisher-Yates) ANTES do sort por peso. Como o
  // `sort` é estável, pares de MESMO peso preservam a ordem embaralhada → o membro
  // escolhido no empate é aleatório. (Antes era determinístico por `user.id`: os
  // `committees` vêm ordenados por id e o sort estável mantinha essa ordem.)
  shuffleInPlace(flatMatrix);

  // sort by decrescent weight (relevance?)
  flatMatrix.sort((a, b) => b.weight - a.weight);

  const [mMin, mMax] = initMaxReviewsPerUser(submissionMatrix.length, numberOfCommittees, numberOfReviewsRequired);

  const phases = [
    {assignmentTest: firstAssignment, max: mMin, motive: Motives.CLAIM},
    {assignmentTest: secondAssignment, max: mMax, motive: Motives.CLAIM},
    {assignmentTest: thirdAssignment, max: mMin, motive: Motives.INTEREST},
    {assignmentTest: fourthAssignment, max: mMax, motive: Motives.INTEREST}
  ];

  // assignment execution of phases
  phases.forEach( (phase) => {
    flatMatrix.forEach(userAssignInfo => {
      const submissionToAssign = userAssignInfo.submissionToAssign;
      const validReviews = submissionToAssign.submission.reviews.filter(r => ![ReviewStatus.DELETED, ReviewStatus.DECLINED].some(s => s === (<Review>r).status));

      if (userAssignInfo.elegible
        && (submissionToAssign.suggestedAssign.length + validReviews.length) < numberOfReviewsRequired
        && phase.assignmentTest(userAssignInfo.submission, userAssignInfo.userPreferences, phase.max)) {
        userAssignInfo.elegible = false;
        userAssignInfo.motive = phase.motive;
        userAssignInfo.userPreferences.reviewing.push(userAssignInfo.submission.id);
        userAssignInfo.submissionToAssign.suggestedAssign.push(userAssignInfo);
      }
    });
  });

  return correctOrder(submissionMatrix);
}


export function initMaxReviewsPerUser(numberOfSubmissions: number, numberOfCommittees: number, numberOfReviewsRequired: number): [number, number] {
  let mMax: number;
  let mMin = (numberOfSubmissions * numberOfReviewsRequired);

  if (mMin > 0) {
    mMin = mMin / numberOfCommittees;
  }

  if (Math.trunc(mMin) !== mMin) {
    mMin = Math.trunc(mMin);
    mMax = mMin + 1;
  } else {
    mMax = mMin;
  }

  return [mMin, mMax];
}


export function correctOrder(matrix: Array<SubmissionAssignSuggestions>): Array<SubmissionAssignSuggestions> {
  matrix.forEach(sub => sub.possiblesAssign.sort((a, b) => a.userPreferences.user.id - b.userPreferences.user.id));
  matrix.sort((a, b) => a.submission.id - b.submission.id);
  return matrix;
}


// Embaralha o array in-place (Fisher-Yates). Usado p/ o desempate ALEATÓRIO do
// flatMatrix antes do sort estável por peso.
export function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}


// Elegibilidade BASE p/ atribuição (independe de bid): não é revisor/atribuidor,
// não é autor, sem conflito com nenhum autor e ainda sob o teto de carga. É a
// condição das fases de "interesse" do clássico (thirdAssignment/fourthAssignment)
// e do preenchimento por afinidade do "Fair bid satisfaction". Exportada p/ reuso
// pelos algoritmos da biblioteca (strategy/*). NÃO inclui hasClaim — o portão de
// bid é responsabilidade de cada fase que precise dele.
export function isEligibleForInterestPhase(submission: Submission, user: UserEvent, maxReviews: number): boolean {
  return !submission.isReviewerOrAssigner(user.user.id) &&
    !submission.isAuthor(user.user.id) &&
    !submission.authors.some(author => user.hasConflict(author.user.id)) &&
    user.reviewing ?.length < maxReviews;
}


// AssignmentTestFunction
function firstAssignment(submission: Submission, user: UserEvent, maxReviews: number): boolean {
  if (submission.hasClaim(user.user.id) &&
    !submission.isReviewerOrAssigner(user.user.id) &&
    !submission.isAuthor(user.user.id) &&
    !submission.authors.some(author => user.hasConflict(author.user.id)) &&
    user.reviewing ?.length < maxReviews
  ) {
    return true;
  }

  return false;
}


// AssignmentTestFunction
function secondAssignment(submission: Submission, user: UserEvent, maxReviews: number): boolean {
  if (submission.hasClaim(user.user.id) &&
    !submission.isReviewerOrAssigner(user.user.id) &&
    !submission.isAuthor(user.user.id) &&
    !submission.authors.some(author => user.hasConflict(author.user.id)) &&
    user.reviewing ?.length < maxReviews
  ) {
    return true;
  }

  return false;
}


// AssignmentTestFunction
function thirdAssignment(submission: Submission, user: UserEvent, maxReviews: number): boolean {
  if (!submission.isReviewerOrAssigner(user.user.id) &&
    !submission.isAuthor(user.user.id) &&
    !submission.authors.some(author => user.hasConflict(author.user.id)) &&
    user.reviewing ?.length < maxReviews
  ) {
    return true;
  }

  return false;
}


// AssignmentTestFunction
function fourthAssignment(submission: Submission, user: UserEvent, maxReviews: number): boolean {
  if (!submission.isReviewerOrAssigner(user.user.id) &&
    !submission.isAuthor(user.user.id) &&
    !submission.authors.some(author => user.hasConflict(author.user.id)) &&
    user.reviewing ?.length < maxReviews
  ) {
    return true;
  }

  return false;
}
